import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowUpRight, ArrowRight, Loader2 } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import StructuredData from "@/components/site/StructuredData";
import { fetchCaseStudyList } from "@/lib/cms";

const SITE_BASE = typeof window !== "undefined" ? window.location.origin : "";

export default function CaseStudiesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const { items: it } = await fetchCaseStudyList();
        if (mounted) setItems(it);
      } catch (e) {
        /* swallow */
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, []);

  const ld = useMemo(() => {
    if (!items.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "CoSec Global Case Studies",
      itemListElement: items.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_BASE}/case-studies/${c.slug}`,
        name: c.headline,
      })),
    };
  }, [items]);

  return (
    <SiteLayout>
      <StructuredData data={ld} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-20 pb-12 max-w-4xl">
          <span className="pill">
            <Sparkles className="h-3.5 w-3.5 text-accent" /> Customer stories
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
            How UK teams use CoSec App to{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">scale compliance.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            Practitioner stories from PE portfolios, accountancy firms, manufacturers and corporate law practices.
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="container-cv">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading case studies…
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              {items.map((c) => (
                <Link
                  key={c.slug}
                  to={`/case-studies/${c.slug}`}
                  data-testid={`case-card-${c.slug}`}
                  className="group relative rounded-3xl border border-border bg-white overflow-hidden hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(14,58,95,0.25)] transition-all"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={c.cover} alt={c.company} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent" />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="px-2.5 py-1 rounded-full bg-accent text-primary text-[10px] font-bold uppercase tracking-wider">{c.sector}</span>
                      <span className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-semibold">{c.region}</span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <div className="text-xs font-bold uppercase tracking-wider text-accent">{c.company}</div>
                      <div className="mt-1 font-display text-xl md:text-2xl font-bold leading-tight">{c.headline}</div>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-muted-foreground leading-relaxed">{c.summary}</p>
                    <div className="mt-5 grid grid-cols-3 gap-3">
                      {(c.metrics || []).map((m, i) => (
                        <div key={i} className="rounded-lg bg-secondary/60 p-2.5 text-center">
                          <div className="font-display text-base font-bold text-primary leading-tight">{m.k}</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{m.l}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-primary group-hover:text-accent transition-colors">
                      Read case study <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
        <div className="container-cv relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.04]">
              Want to be our next{" "}
              <span className="text-accent">case study?</span>
            </h2>
            <p className="mt-3 text-white/70 max-w-2xl">
              We&apos;d love to hear how your team manages UK Cosec work today, and where we can help you do less of it.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Button asChild className="h-12 px-7 bg-accent hover:bg-accent/90 text-primary font-bold">
              <Link to="/contact">Talk to us <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
